import { useEffect, useState } from "react";
import { auth } from "../firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";

export default function AdminGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [allow, setAllow] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  // Build ?next=... safely
  const nextQS = encodeURIComponent(`${loc.pathname}${loc.search || ""}`);
  const goLogin = () => nav(`/admin-login?next=${nextQS}`, { replace: true });

  useEffect(() => {
    let cancelled = false;

    // ENV whitelist (comma-separated emails)
    const WL = String(process.env.REACT_APP_ADMIN_WHITELIST || "")
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const inWhitelist = (email) =>
      !!email && WL.length > 0 && WL.includes(String(email).toLowerCase());

    // Local-session fallback when Firebase user is null / not used
    const checkLocalSession = async () => {
      try {
        const {
          isAuthed,
          hasAdminRole,
          isExpired,
          adminLogout,
          touchSession,
        } = await import("../services/adminAuth");

        // keep session warm a bit
        try { touchSession?.(); } catch {}

        if (isExpired()) {
          try { await adminLogout(); } catch {}
          if (!cancelled) {
            setAllow(false);
            setChecking(false);
            goLogin();
          }
          return;
        }

        const ok = isAuthed() && hasAdminRole();
        if (!cancelled) {
          setAllow(ok);
          setChecking(false);
          if (!ok) goLogin();
        }
      } catch (e) {
        if (!cancelled) {
          setAllow(false);
          setChecking(false);
          goLogin();
        }
      }
    };

    // Prefer Firebase listener (auto-claims refresh)
    const off =
      auth?.onIdTokenChanged?.(async (u) => {
        if (cancelled) return;

        // No Firebase user → try local-session mode
        if (!u) {
          await checkLocalSession();
          return;
        }

        try {
          const t = await u.getIdTokenResult(); // no force each time; onIdTokenChanged already refreshes periodically
          const claims = t?.claims || {};
          const isAdminClaim =
            claims.admin === true ||
            String(claims.role || "").toLowerCase() === "admin";

          const allowed = isAdminClaim || inWhitelist(u.email);
          if (!cancelled) {
            setAllow(allowed);
            setChecking(false);
            if (!allowed) goLogin();
          }
        } catch {
          if (!cancelled) {
            setAllow(false);
            setChecking(false);
            goLogin();
          }
        }
      }) || null;

    return () => {
      cancelled = true;
      if (typeof off === "function") off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, loc.search]); // re-check when route changes

  // Also, on each route change, softly touch local session & auto-logout if expired
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { touchSession, isExpired, adminLogout } = await import("../services/adminAuth");
        try { touchSession?.(); } catch {}
        if (isExpired()) {
          try { await adminLogout(); } catch {}
          if (mounted) goLogin();
        }
      } catch {}
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, loc.search]);

  if (checking) {
    return (
      <div className="min-h-[40vh] grid place-items-center text-gray-600 text-sm">
        Checking access…
      </div>
    );
  }

  return allow ? children : null;
}
